import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Tone = 'accent' | 'nebula' | 'success' | 'warning' | 'danger';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'role' | 'aria-valuenow'> {
  readonly percent: number;
  readonly label?: ReactNode;
  readonly eta?: ReactNode;
  readonly indeterminate?: boolean;
  readonly tone?: Tone;
  readonly ariaLabel?: string;
}

const fillStyles: Record<Tone, string> = {
  accent: 'bg-accent',
  nebula: 'bg-nebula',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

function clampPercent(value: number) {
  if (Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function ProgressBar({
  percent,
  label,
  eta,
  indeterminate = false,
  tone = 'accent',
  ariaLabel,
  className,
  ...rest
}: Props) {
  const pct = clampPercent(percent);
  return (
    <div className={cn('w-full', className)} {...rest}>
      {(label !== undefined || (!indeterminate && percent !== undefined)) && (
        <div className="flex items-center justify-between mb-1.5">
          {label === undefined ? (
            <span />
          ) : (
            <p className="text-sm text-fg-muted">{label}</p>
          )}
          {!indeterminate && (
            <span className="text-xs font-mono text-fg-subtle tabular-nums">
              {pct.toFixed(0)}%
            </span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : pct}
        aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
        aria-busy={indeterminate ? true : pct < 100}
        className="h-2 w-full bg-bg-inset border border-border/60 rounded-full overflow-hidden"
      >
        {indeterminate ? (
          <div className={cn('orb-progress-indeterminate h-full', fillStyles[tone])} />
        ) : (
          <div
            className={cn('h-full transition-[width] duration-300 ease-out', fillStyles[tone])}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      {eta !== undefined && (
        <p className="text-xs text-fg-subtle mt-1">{eta}</p>
      )}
    </div>
  );
}
